import React, { useCallback, useContext, useMemo } from 'react'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { LuxPlusPartnerPromoClassifier } from 'luxPlus/constants/partnerPromo'
import LuxPlusTuroCampaignModal from 'luxPlus/components/PartnerPromoCampaign/TuroCampaign/LuxPlusTuroCampaignModal'
import HelloFreshCampaignModal from 'luxPlus/components/PartnerPromoCampaign/HelloFreshCampaign/HelloFreshCampaignModal'
import TuroLogo from 'luxPlus/components/PartnerPromoCampaign/TuroCampaign/TuroLogo'
import HelloFreshLogo from 'luxPlus/components/PartnerPromoCampaign/HelloFreshCampaign/HelloFreshLogo'
import WineSelectorsLogo from 'luxPlus/components/PartnerPromoCampaign/WineSelectorsCampaign/WineSelectorsLogo'
import WineSelectorsCampaignModal from 'luxPlus/components/PartnerPromoCampaign/WineSelectorsCampaign/WineSelectorsCampaignModal'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'

export interface PartnerPromoOffer {
  id: string
  title: string
  shortTitle: string
  description?: string
  logo: React.ReactNode
  ctaLabel: string
  onViewDetails?: () => void
}

const PARTNER_LOGO_MAP: Record<string, React.ReactNode> = {
  [LuxPlusPartnerPromoClassifier.TURO]: <TuroLogo />,
  [LuxPlusPartnerPromoClassifier.HELLOFRESH]: <HelloFreshLogo />,
  [LuxPlusPartnerPromoClassifier.WINE_SELECTORS]: <WineSelectorsLogo />,
}

const PARTNER_CONTENT_OVERRIDES: Record<string, { title: string; description: string }> = {
  [LuxPlusPartnerPromoClassifier.TURO]: {
    title: '$100 off promo code',
    description: 'Hit the road with the world\'s largest car sharing marketplace.',
  },
  [LuxPlusPartnerPromoClassifier.HELLOFRESH]: {
    title: 'Up to $419 off 10 boxes',
    description: 'Fresh ingredients and chef-designed recipes delivered to your door.',
  },
  [LuxPlusPartnerPromoClassifier.WINE_SELECTORS]: {
    title: '50% off select cases',
    description: 'Premium wine cases curated by Australia\'s leading wine experts.',
  },
}

/**
 * Pulls partner promo offers from the subscription product benefits and
 * matches them with the logged-in member's benefits (when available) to
 * provide modal handlers with real code/expiration data.
 */
function usePartnerPromoOffers(): Array<PartnerPromoOffer> {
  const showModal = useContext(ModalContext)
  const canRedeemPartnerOffers = useAppSelector(getCanRedeemLuxPlusBenefits)
  const { handleBecomeMember } = useLuxPlusLandingActions()

  const subscriptionProduct = useAppSelector(state => state.luxPlus.products.subscription)
  const offer = useMemo(
    () => subscriptionProduct.offers?.find(o => !o.joinFeeWaived),
    [subscriptionProduct.offers],
  )

  const memberBenefits = useAppSelector(
    state => state.auth.account.luxPlus.member.subscription.item?.benefits,
  )

  const memberPartnerBenefits = useMemo(() => {
    if (!memberBenefits) return []
    return memberBenefits.filter(
      (b): b is App.MemberSubscriptionBenefitItem => b.type === 'partner_promo_code' && 'classifier' in b,
    )
  }, [memberBenefits])

  const openModalForClassifier = useCallback((classifier?: string, memberBenefit?: App.MemberSubscriptionBenefitItem) => {
    switch (classifier) {
      case LuxPlusPartnerPromoClassifier.TURO:
        showModal(<LuxPlusTuroCampaignModal benefit={memberBenefit} />)
        break
      case LuxPlusPartnerPromoClassifier.HELLOFRESH:
        showModal(<HelloFreshCampaignModal benefit={memberBenefit} />)
        break
      case LuxPlusPartnerPromoClassifier.WINE_SELECTORS:
        showModal(<WineSelectorsCampaignModal benefit={memberBenefit} />)
        break
    }
  }, [showModal])

  return useMemo(() => {
    if (!offer?.benefits) return []

    const offerPartnerBenefits = offer.benefits.filter(b => b.type === 'partner_promo_code')

    if (offerPartnerBenefits.length > 0) {
      return offerPartnerBenefits.map((benefit, index) => {
        const matchingMember = memberPartnerBenefits.find(mb => mb.classifier === benefit.classifier)
        const classifier = benefit.classifier
        const overrides = classifier ? PARTNER_CONTENT_OVERRIDES[classifier] : undefined
        const onViewDetails = canRedeemPartnerOffers
          ? (classifier ? () => openModalForClassifier(classifier, matchingMember) : undefined)
          : handleBecomeMember
        return {
          id: classifier ?? `partner-${index}`,
          title: overrides?.title ?? benefit.title,
          shortTitle: benefit.shortTitle,
          description: overrides?.description ?? benefit.description,
          logo: (classifier && PARTNER_LOGO_MAP[classifier]) ?? null,
          ctaLabel: canRedeemPartnerOffers ? 'View offer' : 'Unlock offer',
          onViewDetails,
        }
      })
    }

    return memberPartnerBenefits.map(mb => {
      const overrides = mb.classifier ? PARTNER_CONTENT_OVERRIDES[mb.classifier] : undefined
      return {
        id: mb.classifier ?? mb.type,
        title: overrides?.title ?? mb.title,
        shortTitle: mb.shortTitle,
        description: overrides?.description ?? mb.description,
        logo: (mb.classifier && PARTNER_LOGO_MAP[mb.classifier]) ?? null,
        ctaLabel: 'View offer',
        onViewDetails: () => openModalForClassifier(mb.classifier, mb),
      }
    })
  }, [offer?.benefits, memberPartnerBenefits, openModalForClassifier, canRedeemPartnerOffers, handleBecomeMember])
}

export default usePartnerPromoOffers
